/**
 * useErnteprognose (#2341) — the single twin-API interface point for the
 * Ertragsprognose app (APPS_LAYER convention: only the hook touches
 * twinService). Reads three existing twin ENTITY streams and shapes them into
 * the production-agnostic inputs the ``outputSeries``/``horizonOverlay`` pure
 * helpers consume:
 *
 *   • Plan     → ``yield_plan``     (#2344) — a mutable seasonal target scalar.
 *   • Prognose → ``yield_forecast`` (#2333) — current-state forecast per day,
 *                carrying ``generated_at`` for the horizon overlay.
 *   • Ist      → ``operation`` type=pick    — the dated actual pick stream
 *                (high-volume: fetched lazily per selected cycle via ``attr_in``,
 *                NOT the MCP-only ``pick_quantity_by_field`` metric).
 *
 * The "Prognose vor N Tagen" overlay reads each forecast entity's revision
 * series (attribute ``value``, time-keyed on ``valid_from`` = ``generated_at``)
 * so vintages are selected on issue-date, never on transaction time.
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  twinService,
  type DerivedFactListResponse,
  type EntityFacetItem,
  type EntityListResponse,
  type EntitySeriesGroup,
} from '../services/twinService';
import { fetchAllPages } from '../services/paging';
import type { ForecastVintage } from '../pages/apps/ernteprognose/horizonOverlay';
import { currentWirtschaftsjahr } from '../pages/apps/deckungsbeitrag/deckungsbeitragRollup';
import { resolveCurrentSeason } from '../pages/apps/ernteprognose/currentSeason';
import { cropYearNumOf } from '../pages/apps/ernteprognose/lensGroups';

// The backend caps ``limit`` at 1000 per page; MAX_PAGES is a runaway guard.
const PAGE_LIMIT = 1000;
const MAX_PAGES = 10;
// Defensive ``attr_in`` chunking (#2408): a crop-level lens group at Erdbeeren
// scale selects ~100 member ids in one picks fetch — bound each query's id
// filter and concatenate the results (the MAX_PAGES truncate applies per chunk).
const ATTR_IN_CHUNK_SIZE = 50;
// Bound the overlay fan-out (one series call per forecast target day of the
// selected cycle). A berry season is ~150 pick days; 400 leaves generous slack.
const MAX_VINTAGE_ENTITIES = 400;

export const ernteprognoseKeys = {
  all: ['ernteprognose'] as const,
  entities: (entityType: string) => [...ernteprognoseKeys.all, 'entity', entityType] as const,
  config: (factType: string) => [...ernteprognoseKeys.all, 'config', factType] as const,
  picks: (ids: string[]) => [...ernteprognoseKeys.all, 'picks', [...ids].sort().join(',')] as const,
  vintages: (groupKey: string | null, ids: string[]) =>
    [...ernteprognoseKeys.all, 'vintages', groupKey, ids.length] as const,
  // Pick-presence aggregate. #2601/#2610 — no window keys any more: the facet
  // is one unwindowed GROUP BY ("has this cycle ever picked?"), so it no longer
  // varies with the clock or the WJ start month.
  pickPresence: () => [...ernteprognoseKeys.all, 'pickPresence'] as const,
};

/**
 * The WJ start month used when no ``liquiditaet_config`` fact supplies one.
 * WF prod has no such fact (#2601), so this default is what most tenants
 * actually run on — hence ``wjStartMonthSource``, which lets the page DISCLOSE
 * it instead of silently reshaping the chart around an assumption.
 */
export const DEFAULT_WJ_START_MONTH = 7;

/** Whether ``wjStartMonth`` came from the tenant's config fact or the default. */
export type WjStartMonthSource = 'config' | 'default';

/** Shared empty read-only set — a stable default while the aggregate loads. */
const EMPTY_ID_SET: ReadonlySet<string> = new Set<string>();

/**
 * #2599 — facet ``items`` → the crop_cycle_ids whose harvest the chart can
 * actually DRAW.
 *
 * The predicate used to be ``count > 0`` — "does a pick ROW exist?" — while
 * every curve on the page sums ``qty`` (``buildPriorYearSeries``,
 * ``buildOutputChart``). The two are not the same question, and WF proved it:
 * Äpfel (8 picks) and Trauben (3) carry ``qty = 0.0`` on every row back to
 * 2017, so they passed presence, entered the lens, and drew a flat zero line
 * under an "Keine Ernte…" banner. Gating on the SUM makes the presence signal
 * the very quantity the chart cumulates, so the two cannot drift again — the
 * pick-side counterpart of the codex-#2419-P2 ``value != null`` guard already
 * on the forecast branch below.
 *
 * Strictly ``> 0``, with no tolerance band: a measured zero draws nothing just
 * as an artefactual one does, and #2600 (which DROPS zero-Σkg zero-scan op
 * groups at the source) must not be double-compensated for here.
 *
 * ``sum`` ABSENT is not ``sum`` zero. UDB always emits a numeric ``sum`` once
 * it computes one, so a missing field means only that the server predates
 * #2599 — mid-rolling-deploy. Reading that as 0 would empty the farmer's lens
 * for the length of a deploy, so it degrades to the old ``count`` signal.
 *
 * One honest caveat on "the same quantity the chart cumulates": the server's
 * summable grammar is NARROWER than this file's ``numOrNull``. UDB sums JSON
 * numbers and plain numeric strings (``^-?[0-9]+(\.[0-9]+)?$``); ``Number()``
 * here would also take ``'1e3'``, ``'.5'`` or ``' 1.5 '``. A cycle whose qty
 * values were ALL of that exotic shape would sum to 0 (with ``sum_skipped``
 * equal to the count) and be hidden while the chart could still draw it. No
 * twin writer produces those today — ``qty`` arrives as a JSON number — and
 * deliberately NOT compensated for here: an absent or null ``qty`` is skipped
 * too, and for that far more common case hiding is the correct answer, so a
 * ``sum === 0 && sum_skipped === count`` fallback would re-admit exactly the
 * rows #2599 exists to remove.
 */
const facetItemsToDrawableSet = (items: EntityFacetItem[]): Set<string> => {
  const s = new Set<string>();
  for (const it of items) {
    if (it.value == null) continue;
    const drawable = it.sum == null ? it.count > 0 : it.sum > 0;
    if (drawable) s.add(it.value);
  }
  return s;
};

// ── Coercers (twin entity rows are ``Record<string, unknown>``) ─────────────

const strOrNull = (v: unknown): string | null => (typeof v === 'string' && v !== '' ? v : null);
const numOrNull = (v: unknown): number | null => {
  const n = typeof v === 'number' ? v : typeof v === 'string' ? Number(v) : NaN;
  return Number.isFinite(n) ? n : null;
};
/** The flat entity pk lands on ``entity_id``; some types also repeat their own pk key. */
const pkOf = (entity: Record<string, unknown>, ownKey: string): string =>
  (typeof entity[ownKey] === 'string' && (entity[ownKey] as string)) ||
  (typeof entity.entity_id === 'string' ? (entity.entity_id as string) : '');

// ── Mapped shapes ───────────────────────────────────────────────────────────

export interface OutputCycleInfo {
  cropCycleId: string;
  crop: string | null;
  variety: string | null;
  cropYear: string | null;
  fieldId: string | null;
  areaHa: number | null;
}

export interface ForecastEntity {
  forecastId: string;
  cropCycleId: string | null;
  forecastDate: string | null;
  value: number | null;
  generatedAt: string | null;
  measureKind: string | null;
}

export interface PlanEntity {
  planId: string;
  cropCycleId: string | null;
  value: number | null;
  measureKind: string | null;
  planPeriod: string | null;
  asPlannedAt: string | null;
}

const toCycleInfo = (e: Record<string, unknown>): OutputCycleInfo => ({
  cropCycleId: pkOf(e, 'crop_cycle_id'),
  crop: strOrNull(e.crop),
  variety: strOrNull(e.variety),
  cropYear: strOrNull(e.crop_year),
  fieldId: strOrNull(e.field_id),
  areaHa: numOrNull(e.area_ha),
});

const toForecastEntity = (e: Record<string, unknown>): ForecastEntity => ({
  forecastId: pkOf(e, 'yield_forecast_id'),
  cropCycleId: strOrNull(e.crop_cycle_id),
  forecastDate: strOrNull(e.forecast_date),
  value: numOrNull(e.value),
  generatedAt: strOrNull(e.generated_at),
  measureKind: strOrNull(e.measure_kind),
});

const toPlanEntity = (e: Record<string, unknown>): PlanEntity => ({
  planId: pkOf(e, 'yield_plan_id'),
  cropCycleId: strOrNull(e.crop_cycle_id),
  value: numOrNull(e.value),
  measureKind: strOrNull(e.measure_kind),
  planPeriod: strOrNull(e.plan_period),
  asPlannedAt: strOrNull(e.as_planned_at),
});

// ── Eager reads (current-state entities) ────────────────────────────────────

async function fetchAllEntities(entityType: string): Promise<Record<string, unknown>[]> {
  return fetchAllPages(
    async (page) => {
      const res: EntityListResponse = await twinService.queryEntities(entityType, {
        page,
        limit: PAGE_LIMIT,
      });
      return { items: res.entities ?? [], hasMore: res.has_more ?? false };
    },
    { pageCap: MAX_PAGES, onCapExceeded: 'truncate' },
  );
}

function useEntityQuery<T>(
  entityType: string,
  map: (e: Record<string, unknown>) => T,
): { data: T[]; isLoading: boolean; error: Error | null } {
  const query = useQuery({
    queryKey: ernteprognoseKeys.entities(entityType),
    queryFn: () => fetchAllEntities(entityType),
    staleTime: 30_000,
  });
  const data = useMemo(() => (query.data ?? []).map(map), [query.data, map]);
  return { data, isLoading: query.isLoading, error: (query.error as Error | null) ?? null };
}

/** Flat ``field`` entity → its Schlag name (id, name) pair for the per-field table. */
interface FieldName {
  id: string;
  name: string;
}

const toFieldName = (e: Record<string, unknown>): FieldName => ({
  // Flat entity rows expose the pk as ``entity_id``; the attributes JSON does
  // NOT repeat ``field_id`` (unlike crop_cycle rows) — mirror useDeckungsbeitrag.
  id: (typeof e.field_id === 'string' && e.field_id) || (typeof e.entity_id === 'string' ? e.entity_id : ''),
  name: typeof e.name === 'string' ? e.name : '',
});

export interface UseErnteprognoseResult {
  cycles: OutputCycleInfo[];
  forecasts: ForecastEntity[];
  plans: PlanEntity[];
  /** Fiscal-year start month off the retained ``liquiditaet_config`` fact; default 7 (Juli). */
  wjStartMonth: number;
  /**
   * #2601-B — where ``wjStartMonth`` came from. ``'default'`` means no (valid)
   * ``liquiditaet_config`` fact exists and the page MUST disclose the assumption
   * ("Wirtschaftsjahr ab Juli (Standard)") rather than reshape silently.
   */
  wjStartMonthSource: WjStartMonthSource;
  /**
   * #2610 — the harvest season (``crop_year``) the whole page slices on: the
   * wall-clock Wirtschaftsjahr start year, promoted to the current CALENDAR year
   * when — and only when — today is PRE-ROLLOVER (the WJ still runs under last
   * year's label) and that season's cycles already carry picks. THE single
   * slicing input: Ist, banner, chips, table, lens and overlays all derive from
   * it, so they cannot disagree (#2413).
   */
  currentSeason: number;
  /** field_id → Schlag name (nameless / unresolved fields omitted → "Ohne Schlag"). */
  fieldNames: Record<string, string>;
  /**
   * #2419/#2610/#2599 — crop_cycle_ids whose picks sum to a DRAWABLE quantity
   * (Σ``qty`` > 0), at ANY date.
   *
   * Season-agnostic by design: which season a pick belongs to is the cycle's
   * ``crop_year``, so this set answers only "does this cycle have harvest at
   * all" — the drawable signal for the current Ist AND the −1/−2/−3 overlays
   * (which shift whole years and so draw whatever the cycle carries).
   *
   * #2599 changed the MEASURE, not the shape: this was "≥1 ``operation
   * type=pick`` row exists", which let a cycle whose picks all carry
   * ``qty = 0`` (WF Äpfel, Trauben) into the lens to draw nothing. It is now
   * the same quantity the chart cumulates, so every consumer — the lens
   * predicate, the prior-year offset toggles, and ``resolveCurrentSeason``'s
   * "is this season demonstrably harvesting" evidence — asks the one question
   * they all meant: can the chart draw this?
   */
  pickCycles: ReadonlySet<string>;
  /**
   * #2419/#2610 — crop_cycle_ids with a CURRENT-SEASON forecast/plan (a
   * ``yield_forecast`` on a ``crop_year === currentSeason`` cycle OR a
   * ``yield_plan`` whose ``plan_period == currentSeason``). Keeps a crop the
   * farmer is actively forecasting at season start visible before the first pick.
   */
  cyclesWithCurrentForecast: ReadonlySet<string>;
  /**
   * #2419 — React-Query status of the pick-presence aggregate, driving the
   * lens tri-state (loading placeholder / empty state / fail-open). Distinct
   * from ``isLoading`` (the eager entity feed) so the page can tell a genuine
   * empty result from a load in flight or a failure.
   */
  pickPresenceStatus: 'loading' | 'success' | 'error';
  isLoading: boolean;
  error: Error | null;
}

export function useErnteprognose(): UseErnteprognoseResult {
  const cycleQuery = useEntityQuery('crop_cycle', toCycleInfo);
  const forecastQuery = useEntityQuery('yield_forecast', toForecastEntity);
  const planQuery = useEntityQuery('yield_plan', toPlanEntity);
  const fieldQuery = useEntityQuery('field', toFieldName);
  // The WJ start month has no account home (a planning preference); read-of-record
  // is the retained ``liquiditaet_config`` derived fact — mirror useDeckungsbeitrag.
  const configQuery = useQuery({
    queryKey: ernteprognoseKeys.config('liquiditaet_config'),
    queryFn: (): Promise<DerivedFactListResponse> =>
      twinService.queryDerivedFacts({ fact_type: 'liquiditaet_config', limit: 1 }),
    staleTime: 30_000,
  });

  // #2601-B — resolve the month AND where it came from together, so a missing or
  // out-of-range config can be disclosed rather than silently assumed.
  const { wjStartMonth, wjStartMonthSource } = useMemo((): {
    wjStartMonth: number;
    wjStartMonthSource: WjStartMonthSource;
  } => {
    const fv = configQuery.data?.derived_facts?.[0]?.fact_value;
    const raw = fv && typeof fv === 'object' ? (fv as Record<string, unknown>).wj_start_month : undefined;
    const n = typeof raw === 'number' ? raw : typeof raw === 'string' ? Number(raw) : NaN;
    return Number.isFinite(n) && n >= 1 && n <= 12
      ? { wjStartMonth: n, wjStartMonthSource: 'config' }
      : { wjStartMonth: DEFAULT_WJ_START_MONTH, wjStartMonthSource: 'default' };
  }, [configQuery.data]);

  const fieldNames = useMemo(() => {
    const names: Record<string, string> = {};
    for (const f of fieldQuery.data) if (f.id && f.name) names[f.id] = f.name;
    return names;
  }, [fieldQuery.data]);

  // ── Pick-presence aggregate (#2419, re-shaped by #2601/#2610) ────────────
  // ONE cheap lens-wide GROUP BY (NOT N per-group pick fetches): every
  // crop_cycle with ≥1 ``operation type=pick``. It carries NO dateRange any
  // more. Under season slicing the Ist of a season is the FULL pick span of its
  // ``crop_year`` cycles, so "can the chart draw this cycle?" is exactly "does
  // it have a pick at all" — a wall-clock window here is precisely what made WF
  // Spargel's Mar–Jun season invisible after the July rollover (#2610) and
  // bisected Erdbeeren in April (#2601). WHICH season a cycle belongs to is
  // decided client-side from its ``crop_year`` (``hasDrawableMember``), which is
  // where that knowledge lives. Collapsing the two windowed facets into one
  // also halves the aggregate round-trips.
  //
  // #2599 — it asks for Σ``qty`` per cycle, not just the row count. The count
  // answered "does a pick row exist?"; every curve on this page sums ``qty``.
  // See ``facetItemsToDrawableSet`` for why that gap is the whole bug.
  const pickAggregateQuery = useQuery({
    queryKey: ernteprognoseKeys.pickPresence(),
    staleTime: 30_000,
    queryFn: async () => {
      const res = await twinService.getEntityFacetsFiltered('operation', {
        attribute: 'crop_cycle_id',
        filters: { type: 'pick' },
        sumAttribute: 'qty',
      });
      return facetItemsToDrawableSet(res.items);
    },
  });

  const pickCycles = pickAggregateQuery.data ?? EMPTY_ID_SET;
  const pickPresenceStatus: 'loading' | 'success' | 'error' = pickAggregateQuery.isError
    ? 'error'
    : pickAggregateQuery.isSuccess
      ? 'success'
      : 'loading';

  // The season the whole page slices on (#2610). Base = the wall-clock WJ start
  // year; promoted to the current CALENDAR year, and ONLY in the pre-rollover
  // window (``baseWj < todayYear``, i.e. today's month precedes wj_start_month),
  // and ONLY when that season's cycles ALREADY carry picks — an early crop
  // harvesting before the WJ rolls over. See ``resolveCurrentSeason`` for the
  // full rule, including why it deliberately does not fail open.
  //
  // Both values come from ONE ``new Date()``: ``currentWirtschaftsjahr`` reads
  // local calendar fields, so deriving the year from a UTC ISO string instead
  // would let the two disagree for the hours around New Year in a non-UTC zone
  // and spuriously satisfy ``baseWj < todayYear``.
  const { baseWj, todayYear } = useMemo(() => {
    const now = new Date();
    return { baseWj: currentWirtschaftsjahr(wjStartMonth, now), todayYear: now.getFullYear() };
  }, [wjStartMonth]);
  const currentSeason = useMemo(
    () => resolveCurrentSeason(cycleQuery.data, pickCycles, baseWj, todayYear),
    [cycleQuery.data, pickCycles, baseWj, todayYear],
  );

  /** crop_cycle ids belonging to the CURRENT season (``crop_year === currentSeason``). */
  const currentSeasonCycles = useMemo(() => {
    const s = new Set<string>();
    for (const c of cycleQuery.data) if (cropYearNumOf(c) === currentSeason) s.add(c.cropCycleId);
    return s;
  }, [cycleQuery.data, currentSeason]);

  // Current-SEASON forecast/plan presence (#2419 predicate, re-anchored by
  // #2610) — free (the eager feed is already loaded): a ``yield_forecast`` on a
  // current-season crop_cycle, OR a ``yield_plan`` whose plan_period ==
  // currentSeason. The forecast branch keys on the CYCLE's season rather than
  // the forecast_date, because a spring forecast of an early crop falls outside
  // a July WJ window exactly the way its picks do — same defect, same fix.
  // Prior- and next-season forecasts/plans still do NOT qualify.
  // Both branches also require a non-null ``value`` (codex #2419 P2): the chart
  // (``forecastPoints``) and table drop value-less rows, so a cycle with only an
  // EMPTY current forecast/plan draws nothing — marking it drawable here would
  // re-open the "lens shows a crop the chart can't draw" gap this task closes.
  const cyclesWithCurrentForecast = useMemo(() => {
    const s = new Set<string>();
    for (const f of forecastQuery.data) {
      if (f.cropCycleId && f.value != null && currentSeasonCycles.has(f.cropCycleId)) s.add(f.cropCycleId);
    }
    for (const p of planQuery.data) {
      // Symmetric with the forecast branch above (codex-equivalent review P3):
      // the CYCLE's season decides membership, and the plan must additionally be
      // planned FOR that season. A plan_period pointing at a season its own
      // cycle does not belong to is not evidence about this season.
      if (
        p.cropCycleId &&
        p.value != null &&
        p.planPeriod != null &&
        Number(p.planPeriod) === currentSeason &&
        currentSeasonCycles.has(p.cropCycleId)
      ) {
        s.add(p.cropCycleId);
      }
    }
    return s;
  }, [forecastQuery.data, planQuery.data, currentSeasonCycles, currentSeason]);

  const isLoading =
    cycleQuery.isLoading ||
    forecastQuery.isLoading ||
    planQuery.isLoading ||
    fieldQuery.isLoading ||
    configQuery.isLoading;
  // A missing yield_plan / field / config source is a tolerated runtime state
  // (grey the Plan line, "Ohne Schlag", default WJ), NOT a page error — only a
  // crop_cycle / yield_forecast failure blanks the whole app.
  const error = cycleQuery.error ?? forecastQuery.error ?? null;

  return {
    cycles: cycleQuery.data,
    forecasts: forecastQuery.data,
    plans: planQuery.data,
    wjStartMonth,
    wjStartMonthSource,
    currentSeason,
    fieldNames,
    pickCycles,
    cyclesWithCurrentForecast,
    pickPresenceStatus,
    isLoading,
    error,
  };
}

// ── Lazy per-cycle Ist (picks) ──────────────────────────────────────────────

export interface CyclePick {
  /** Owning crop_cycle — carried so a multi-id group fetch can split picks back
   *  per field-cycle (the per-field table + the prior-year overlays need this). */
  cropCycleId: string | null;
  date: string;
  qty: number;
  unit: string | null;
}

/**
 * Lazy pick stream (Ist) for ONE crop_cycle or a WHOLE crop-level lens group.
 * ``operation`` is a high-volume entity (excluded from embeddings), so it is
 * never fetched up front — only the selected cycles' ``type='pick'`` rows,
 * server-side filtered via ``attr_in`` (``crop_cycle_id`` accepts a ``string[]``).
 * The id list is sorted (reordered member lists share one cache entry and one
 * query shape) and chunked at ``ATTR_IN_CHUNK_SIZE`` (#2408) — a crop-level
 * group at Erdbeeren scale can select ~100 cycles, and each chunk keeps the
 * page-cap truncate risk bounded per query rather than across the whole group.
 * ``enabled`` lets a caller keep a fetch closed until it is needed (the
 * prior-year overlay opens only when a ``?py=`` toggle is on).
 */
export function useCyclePicks(
  ids: string | string[] | null,
  enabled = true,
): { picks: CyclePick[]; isLoading: boolean } {
  const idList = useMemo(
    () => (ids == null ? [] : (Array.isArray(ids) ? ids : [ids]).filter(Boolean)).sort(),
    [ids],
  );
  const query = useQuery({
    queryKey: ernteprognoseKeys.picks(idList),
    enabled: enabled && idList.length > 0,
    staleTime: 30_000,
    queryFn: async () => {
      const chunks: string[][] = [];
      for (let i = 0; i < idList.length; i += ATTR_IN_CHUNK_SIZE) {
        chunks.push(idList.slice(i, i + ATTR_IN_CHUNK_SIZE));
      }
      const perChunk = await Promise.all(
        chunks.map((chunk) =>
          fetchAllPages(
            async (page) => {
              const res: EntityListResponse = await twinService.queryEntities('operation', {
                attr_in: { crop_cycle_id: chunk, type: ['pick'] },
                page,
                limit: PAGE_LIMIT,
              });
              return { items: res.entities ?? [], hasMore: res.has_more ?? false };
            },
            { pageCap: MAX_PAGES, onCapExceeded: 'truncate' },
          ),
        ),
      );
      return perChunk
        .flat()
        .filter((e) => e.type === 'pick')
        .map((e) => ({
          cropCycleId: strOrNull(e.crop_cycle_id),
          date: strOrNull(e.date),
          qty: numOrNull(e.qty),
          unit: strOrNull(e.unit),
        }))
        .filter((p): p is CyclePick => p.date != null && p.qty != null);
    },
  });
  return { picks: query.data ?? [], isLoading: query.isLoading };
}

// ── Lazy per-cycle forecast vintages (horizon overlay) ──────────────────────

/** First value of a series group's time key (``valid_from`` for an attribute series). */
const groupTime = (group: EntitySeriesGroup): string | null => {
  const first = Object.values(group.key ?? {})[0];
  return typeof first === 'string' ? first : null;
};

/**
 * Lazy per-cycle forecast revision pool for the "Prognose vor N Tagen" overlay.
 * For each of the cycle's forecast entities, reads the ``value`` attribute
 * series — one group per revision, time-keyed on ``valid_from`` (which IS the
 * forecast's ``generated_at``) — and flattens to ``ForecastVintage`` records
 * the pure ``buildHorizonForecastPoints`` selector consumes. Gated on
 * ``enabled`` (the overlay toggle) so the fan-out never runs unopened.
 */
export function useForecastVintages(
  cropCycleId: string | null,
  forecastEntities: { forecastId: string; forecastDate: string | null }[],
  enabled: boolean,
): { vintages: ForecastVintage[]; isLoading: boolean; truncated: boolean } {
  const ids = useMemo(
    () => forecastEntities.map((f) => f.forecastId).filter(Boolean),
    [forecastEntities],
  );
  const truncated = ids.length > MAX_VINTAGE_ENTITIES;
  const dateById = useMemo(() => {
    const m = new Map<string, string>();
    for (const f of forecastEntities) if (f.forecastId && f.forecastDate) m.set(f.forecastId, f.forecastDate);
    return m;
  }, [forecastEntities]);

  const query = useQuery({
    queryKey: ernteprognoseKeys.vintages(cropCycleId, ids),
    enabled: enabled && cropCycleId != null && ids.length > 0,
    staleTime: 30_000,
    queryFn: async () => {
      const capped = ids.slice(0, MAX_VINTAGE_ENTITIES);
      const perEntity = await Promise.all(
        capped.map(async (id) => {
          const forecastDate = dateById.get(id);
          if (!forecastDate) return [] as ForecastVintage[];
          const series = await twinService.getEntitySeries('yield_forecast', id, { attribute: 'value' });
          const out: ForecastVintage[] = [];
          for (const group of series.groups ?? []) {
            const generatedAt = groupTime(group);
            const value = numOrNull(group.measures?.value);
            if (generatedAt != null && value != null) out.push({ forecastDate, generatedAt, value });
          }
          return out;
        }),
      );
      return perEntity.flat();
    },
  });

  return { vintages: query.data ?? [], isLoading: query.isLoading, truncated };
}
